import { Box, Typography, Chip } from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { useMemo, useCallback } from 'react'
import { AssignmentProgressMatrix } from '../types'

export type ProgressMatrixProps = {
  matrixData: AssignmentProgressMatrix | null
  height?: number
}

export default function ProgressMatrix({
  matrixData,
  height = 500,
}: ProgressMatrixProps) {
  let ProgressMatrixToolbar = useCallback(
    () => <DataGridToolbar fileName="progress-matrix" />,
    [],
  )

  let { tableData, columns, matrixCompletions, matrixPossible } =
    useMemo(() => {
      if (!matrixData) {
        return {
          tableData: [],
          columns: [],
          matrixCompletions: 0,
          matrixPossible: 0,
        }
      }

      // Create columns - userName + one column per course (FLEX 1 FOR ALL!)
      // eslint-disable-next-line no-shadow
      let columns: GridColDef[] = [
        { field: 'userName', headerName: 'User Name', flex: 1 },
        ...matrixData.courses.map((course) => ({
          field: course.courseTitle,
          headerName: course.courseTitle,
          align: 'center' as const,
          headerAlign: 'center' as const,
          flex: 1,
          renderCell: (params: any) =>
            params.value ? (
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
            ) : (
              <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />
            ),
        })),
      ]

      // Create simple flat data for DataGrid - NO ID FIELD IN DATA
      // eslint-disable-next-line no-shadow
      let tableData = matrixData.users?.length
        ? matrixData.users.map((user, userIndex) => {
            let row: any = {
              userName: user.userName || '',
            }

            // Add each course completion as boolean values (renderCell will handle the icons)
            if (matrixData.courses && matrixData.matrix) {
              matrixData.courses.forEach((course, courseIndex) => {
                let cellData = matrixData.matrix[userIndex]?.[courseIndex]
                if (cellData) {
                  // eslint-disable-next-line fp/no-mutation
                  row[course.courseTitle] = cellData.completed
                }
              })
            }

            return row
          })
        : []

      // Calculate statistics
      let calculatedPossible =
        matrixData.users.length * matrixData.courses.length
      let calculatedCompletions = matrixData.matrix
        .flat()
        .filter((cell) => cell.completed).length

      return {
        tableData,
        columns,
        matrixCompletions: calculatedCompletions,
        matrixPossible: calculatedPossible,
      }
    }, [matrixData])

  if (!matrixData) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No matrix data available
        </Typography>
      </Box>
    )
  }

  let completionPercentage =
    matrixPossible > 0
      ? Math.round((matrixCompletions / matrixPossible) * 100)
      : 0

  return (
    <Box>
      {/* Header with search and stats */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Chip
            label={`${matrixData.users.length} Users`}
            color="primary"
            size="small"
          />
          <Chip
            label={`${matrixData.courses.length} Courses`}
            color="secondary"
            size="small"
          />
          <Chip
            label={`${matrixCompletions}/${matrixPossible} Completions (${completionPercentage}%)`}
            color={completionPercentage >= 80 ? 'success' : 'warning'}
            size="small"
          />
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Legend:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
          <Typography variant="caption">Completed</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CancelIcon sx={{ color: 'error.main', fontSize: 16 }} />
          <Typography variant="caption">Not Completed</Typography>
        </Box>
      </Box>

      {/* Matrix Grid */}
      <Box sx={{ height }}>
        <DataGridPremium
          rows={tableData}
          columns={columns}
          slots={{ toolbar: ProgressMatrixToolbar }}
          showToolbar
          getRowId={(row) => row.userName}
        />
      </Box>
    </Box>
  )
}
